/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Servers sub-tab — list servers from sftp.yml, show enable/disable
 * toggle (controls whether the autoban loop polls this server), and
 * "Test Connection" button that probes SFTP, banlist.txt, cheats dir,
 * and RCON in one go.
 */
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Server, CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function ServersTab({ toast }) {
  const [servers, setServers] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/moderation/servers");
      setServers(data.servers || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const test = async (name) => {
    setBusy(`test-${name}`);
    try {
      const { data } = await api.post(`/admin/moderation/servers/${encodeURIComponent(name)}/test`);
      const ok = data.sftp && data.rcon;
      if (ok) toast.success(`${name}: all good (SFTP + RCON + banlist + cheats dir)`);
      else toast.error(`${name}: SFTP=${data.sftp} RCON=${data.rcon} banlist=${data.banlist} cheats=${data.cheats_dir}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const toggle = async (name, disabled) => {
    setBusy(`toggle-${name}`);
    try {
      await api.post(`/admin/moderation/servers/${encodeURIComponent(name)}/toggle?disabled=${disabled}`);
      toast.success(`${name}: ${disabled ? "disabled" : "enabled"} for auto-detection`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const renderTest = (lastTest) => {
    if (!lastTest) return null;
    try {
      const t = JSON.parse(lastTest);
      const items = [
        ["SFTP", t.sftp],
        ["RCON", t.rcon],
        ["banlist.txt", t.banlist],
        ["cheats dir", t.cheats_dir],
      ];
      return (
        <div className="flex flex-wrap gap-2 mt-2">
          {items.map(([label, ok]) => (
            <span key={label} className={`flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
              ok ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"
            }`}>
              {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {label}
            </span>
          ))}
          {t.rcon_info ? <span className="text-[10px] text-[var(--muted)] font-mono">{t.rcon_info}</span> : null}
        </div>
      );
    } catch { return null; }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-[var(--muted)]">
        Servers are loaded from <code>/app/backend/data/sftp.yml</code>. Toggling a server off means the
        auto-detection loop will skip it; manual bans still sync.
      </p>
      {servers.length === 0 ? (
        <div className="glass-card p-6 text-[var(--muted)] text-sm">No servers configured.</div>
      ) : servers.map((s) => (
        <div key={s.name} className="glass-card p-4" data-testid={`server-row-${s.name}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Server size={14} className="text-[var(--teal)]" />
                <span className="text-base text-[var(--bone)]">{s.name}</span>
                {s.disabled ? (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300">Disabled</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">Auto-detect ON</span>
                )}
              </div>
              <div className="text-xs text-[var(--muted)] mt-1 font-mono">
                {s.host} · SFTP :{s.sftp_port} · RCON :{s.rcon_port}
              </div>
              <div className="text-[10px] text-[var(--muted)] mt-0.5 font-mono break-all">
                cheats: {s.cheats_dir || "—"} · banlist: {s.banlist_path}
              </div>
              {renderTest(s.last_test)}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => test(s.name)}
                disabled={busy === `test-${s.name}`}
                className="btn btn-ghost"
                data-testid={`server-test-${s.name}`}
              >
                {busy === `test-${s.name}` ? <Loader2 size={14} className="animate-spin" /> : "Test"}
              </button>
              <button
                onClick={() => toggle(s.name, !s.disabled)}
                disabled={busy === `toggle-${s.name}`}
                className={`btn ${s.disabled ? "btn-teal" : "btn-ghost"}`}
                data-testid={`server-toggle-${s.name}`}
              >
                {s.disabled ? "Enable" : "Disable"}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
